/** Standardized operations dashboard colors — use across grid, badges, heat map, kanban, alerts. */

export const OPS_COLORS = {
  present: '#2e7d32',
  late: '#f9a825',
  coverage: '#1565c0',
  uncovered: '#ef6c00',
  critical: '#c62828',
  scheduledOff: '#757575',
  expected: '#546e7a',
  excellent: '#2e7d32',
  good: '#43a047',
  warning: '#f9a825',
  risk: '#ef6c00',
  criticalScore: '#c62828',
} as const;

export const ATTENDANCE_CHIP_COLORS: Record<string, string> = {
  PRESENT: OPS_COLORS.present,
  SLIGHTLY_LATE: '#64b5f6',
  LATE: '#ef6c00',
  ABSENT: OPS_COLORS.critical,
  NO_CALL_NO_SHOW: OPS_COLORS.critical,
  SCHEDULED_OFF: OPS_COLORS.scheduledOff,
  EXPECTED: OPS_COLORS.expected,
};

export const COVERAGE_CHIP_COLORS: Record<string, string> = {
  COVERED: OPS_COLORS.coverage,
  PARTIALLY_COVERED: OPS_COLORS.coverage,
  NOT_COVERED: OPS_COLORS.uncovered,
  NOT_REQUIRED: OPS_COLORS.scheduledOff,
};

export function getOperationalScoreHeatColor(score: number): string {
  if (score >= 100) return OPS_COLORS.excellent;
  if (score >= 90) return OPS_COLORS.good;
  if (score >= 75) return OPS_COLORS.risk;
  return OPS_COLORS.criticalScore;
}

export function getOperationalScoreEmoji(score: number): string {
  if (score >= 100) return '🟢';
  if (score >= 90) return '🟡';
  if (score >= 75) return '🟠';
  return '🔴';
}

export type CampaignHealthLevel = 'healthy' | 'monitor' | 'warning' | 'critical' | 'not_scheduled';

/** Staffing health thresholds: 100% healthy, 90–99% monitor, 75–89% warning, below 75% critical. */
export function getCampaignHealthLevel(score: number, scheduled: number): CampaignHealthLevel {
  if (scheduled <= 0) return 'not_scheduled';
  if (score >= 100) return 'healthy';
  if (score >= 90) return 'monitor';
  if (score >= 75) return 'warning';
  return 'critical';
}

export function getCampaignHealthLabel(level: CampaignHealthLevel): string {
  switch (level) {
    case 'healthy': return 'Healthy';
    case 'monitor': return 'Monitor';
    case 'warning': return 'Staffing Risk';
    case 'critical': return 'Critical';
    default: return 'Not Scheduled';
  }
}

export function getCampaignHealthRowBg(level: CampaignHealthLevel): string {
  switch (level) {
    case 'healthy': return `${OPS_COLORS.excellent}14`;
    case 'monitor': return `${OPS_COLORS.warning}1a`;
    case 'warning': return `${OPS_COLORS.risk}18`;
    case 'critical': return `${OPS_COLORS.criticalScore}14`;
    default: return 'action.hover';
  }
}

export function getStaffingProgressColor(percent: number): string {
  if (percent >= 100) return OPS_COLORS.excellent;
  if (percent >= 90) return OPS_COLORS.warning;
  if (percent >= 75) return OPS_COLORS.risk;
  return OPS_COLORS.criticalScore;
}

export function getReadinessLabel(score: number): string {
  if (score >= 100) return 'Fully Staffed';
  if (score >= 95) return 'Minor Coverage Needed';
  if (score >= 80) return 'Staffing Risk';
  return 'Critical Staffing Issue';
}

export function getPriorityColor(priority: string): string {
  if (priority === 'high') return OPS_COLORS.critical;
  if (priority === 'medium') return OPS_COLORS.uncovered;
  return OPS_COLORS.coverage;
}
